import { CommentDetailProps } from "./commentDetail";
import { Toast as showToast } from "core/components/global/toast/toast";
import { useTranslation } from "react-i18next";
import { AuthContext } from "core/contexts/auth/auth";
import { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { getAlertDetailsByIdService } from "modules/ac2/services/alertService";
import { useCopyToClipboard } from "core/hooks/copytoClipboard";
import { useAppDispatch, useAppSelector } from "core/store/reduxStore";
import { IconX, IconMaximize, IconMinimize, IconAlertCircle } from "@tabler/icons-react";
import { setPreviewWidth, setSideModal } from "modules/ac2/store/alert";

import Markdown from 'markdown-to-jsx'
import {
  AlertSummaryContainer,
  TagsButton,
  TextContainer,
  CommentsContainer,
  AlertDetailsContainer,
  PanelContainer,
  MarkdownContainer,
  ActionButtonsContainer,
  CloseBtnContainer,
  CloseBtn,
  CloudFormation,
  AWSCLIContainer,
  TerraformContainer,
  ReprocessButton
} from "./alertSummary.styled";
import { 
  AccordionSummary,
  Paper, 
  Skeleton, 
  Table,
  TableBody, 
  TableCell, 
  TableContainer, 
  TableRow, 
  Typography 
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { UserInput } from "./alertInput";
import { ControlsDetail } from './alertControlsDetail';
import AlertComments from "./alertComments";
import CrossBtnIcon from "assets/CrossBtnIcon";
import { useTheme } from 'styled-components';
import CustomMarkdown from "core/components/global/markdown/markdown";
import StandaloneChatInterface from "./StandaloneChatInterface";


export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

export interface IRelatedAlerts {
  id: string;
  description?: string;
  createdDate?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number | string;
  value: number | string;
}

interface AlertDetailsAccordionProps {
  assigned?: string[];
  tags?: any[];
  org?: string;
  lastModified: string;
  createdDate: string;
}

export function AlertDetailsAccordion({assigned, tags, org, lastModified, createdDate}:AlertDetailsAccordionProps) {
  return (
    <div className="w-100 p-0">
      <AlertDetailsContainer className="shadow-xl my-2">
        <AccordionSummary
          component={Paper}
          elevation={1}
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Typography>Alert Details</Typography>
        </AccordionSummary>
        <TableContainer component={Paper} elevation={2}>
          <Table width={690} aria-label="alert-details">
            <TableBody>
                <TableRow
                  key={0}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">AWS Org</TableCell>
                  {
                    org ?
                    <TableCell component="th" scope="row">{org}</TableCell> : <TableCell></TableCell>
                  }
                </TableRow>
                <TableRow
                  key={2}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">Assigned User(s)</TableCell>
                  <TableCell component="th" scope="row">
                    {assigned?.map((name) => (
                      <span key={name}>
                        {name}
                      </span>
                    ))}
                  </TableCell>
                </TableRow>
                <TableRow
                  key={3}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">Last Modified</TableCell>
                  <TableCell component="th" scope="row">{lastModified}</TableCell>
                </TableRow>
                <TableRow
                  key={4}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">Created</TableCell>
                  <TableCell component="th" scope="row">{createdDate}</TableCell>
                </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </AlertDetailsContainer>
    </div>
  );
}

export interface StandardMapping {
  Object(any);
}

export interface AlertSummarySchema {
  id: string;
  severity: string;
  status: string;
  classification: string;
  organization?: string;
  assignedUsers?: string[];
  description: string;
  standardMapping: any;
  awsBestPractice: string;
  terraformRemediation: string;
  awsCliRemediation: any;
  cloudformationTemplate: string;
  createdDate: string;
  lastModified: string;
  comments: CommentDetailProps[];
  findingid: string;
  tags?: any[];
  relatedAlerts?: any[]
}

export interface AlertSummaryProps {
  setOpenSideModal: Dispatch<SetStateAction<boolean>>;
  selectedId: string;
}

export const AlertSummary = ({
  setOpenSideModal,
  selectedId,
}: AlertSummaryProps) => {
  const theme = useTheme();
  const { afUser: user } = useContext(AuthContext);
  const { t } = useTranslation();
  const [alertSummary, setAlertSummary] = useState<AlertSummarySchema>();
  const [ isLoadingGeneral, setIsLoadingGeneral ] = useState(true);
  const [ errorState, setErrorState ] = useState(false);
  const { checkAFToken } = useContext(AuthContext)
  const [tabValue, setTabValue] = useState({"actions": 0, "response": 0, "gap_analysis": ""});
  const [copiedText, copyToClipboard] = useCopyToClipboard();
  const dispatch = useAppDispatch();
  const alertState = useAppSelector((state) => state.alerts);
  const [isFullWidth, setIsFullWidth] = useState(false);
  const [prevWidth, setPrevWidth] = useState(50);
  
  /**
   * On component initiation, the data for this will be fetched
   */
  useEffect(() => {
    const fetchData = async (id) => {  
      const token = await checkAFToken();
      const config = { 'Authorization': token, 'Content-Type': 'application/json' };
      try {
        getAlertDetailsByIdService(config, id).then((response) => {
          console.log("Alert Summary Response:", response);
          if (response) {
            setAlertSummary(response);
            setErrorState(false);
          } else {
            
            setErrorState(true);
          }
          setIsLoadingGeneral(false);
        }).catch(error => {
          console.error("Error fetching alert details:", error);
          
          setIsLoadingGeneral(false);
          setErrorState(true);
        });
      } catch (error) {
        console.error("Error in fetchData:", error);
        setIsLoadingGeneral(false);
        setErrorState(true);
      }
    };
  
    fetchData(selectedId);
    console.log("Alert Summary Selected ID:", selectedId);
  }, [selectedId])

  /**
   * Either point to a new tab to show or point to no tab
   * 0 will show no sections, any other number with an assoicated
   * section will show that section
   * @param newValue 
   */
  const handleTabChange = (newValue: number | string, type: string) => {
    if (type in tabValue) {
      if (newValue === tabValue[type]) {
        setTabValue((current) => ({
          ...current,
          [type]: 0
        }));    
      } else {
        setTabValue((current) => ({
          ...current,
          [type]: newValue
        }));
      }
    }
  };

  const RefetchAlertDetails = async (id) => {  
      const token = await checkAFToken();
      const config = { 'Authorization': token, 'Content-Type': 'application/json' };
      try {
        getAlertDetailsByIdService(config, id).then((response) => {
          console.log("Alert Summary Response:", response);
          if (response) {
            setAlertSummary(response);
            setErrorState(false);
          } else {
            
            setErrorState(true);
          }
          setIsLoadingGeneral(false);
        }).catch(error => {
          console.error("Error fetching alert details:", error);
          
          setIsLoadingGeneral(false);
          setErrorState(true);
        });
      } catch (error) {
        console.error("Error in fetchData:", error);
        setIsLoadingGeneral(false);
        setErrorState(true);
      }
    };

  const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        style={{width: "inherit"}}
        {...other}
      >
        {value === index && (
          <PanelContainer>
            {children}
          </PanelContainer>
        )}
      </div>
    );
  };

  useEffect(() => {
    const copyText = async () => {
      if (copiedText) {
        const success = await copyToClipboard(copiedText);
        if (!success) {
          console.log("Copy failed, no permission or clipboard support, update your browser permissions.");
        }
      }
    };

    copyText();
  }, [copiedText, copyToClipboard]);
  
  const [statusColor, setStatusColor] = useState(Object());

  const statusMapping = {
    [t("STATUS.OPEN")]: {
      "bk": theme.colors.open_incident,
      "text": theme.colors.textColor
    },
    [t("STATUS.IN_PROGRESS")]: {
      "bk": theme.colors.in_progress_incident,
      "text": theme.colors.textColor
    },
    [t("STATUS.CLOSED")]: {
      "bk": theme.colors.closed_incident,
      "text": theme.colors.textColor
    },
    [t("STATUS.REQUEST_TUNING")]: {
      "bk": theme.colors.requested_incident,
      "text": theme.colors.textColor
    }
  }

  useEffect(() => {
    if (alertSummary?.status && statusMapping[alertSummary?.status]) {
      setStatusColor({
        text: statusMapping[alertSummary?.status].text,
        bk: statusMapping[alertSummary?.status].bk
      });
    }
  }, [alertSummary?.status]);

  const splitCodeAndText = (inputText) => {
    // Split by code blocks marked by ```
    const parts = inputText.split(/(```[\s\S]*?```)/gm);
    const jsxElements: any = [];

    parts.forEach((part, index) => {
      if (part.startsWith('```') && part.endsWith('```')) {
        // Code block: Wrap in CustomMarkdown
        jsxElements.push(
          <CustomMarkdown content={part.replace(/```/g, '')} showEditButton={false} />
        );
      } else {
        // Non-code block: Wrap in Markdown
        jsxElements.push(
          <Markdown key={index}>
            {part}
          </Markdown>
        );
      }
    });

    return jsxElements;
  };

  interface ToggleFullWidthProps {
    close?: boolean;
  }

  // Toggle between full width and previous width
  const toggleFullWidth = ({close}: ToggleFullWidthProps = {}) => {
    try {
      if (close) {
        // Set the side modal to closed
        dispatch(setSideModal(false));
      } else {
        if (isFullWidth) {
          // Return to previous width using Redux
          if (prevWidth > 10) {
            dispatch(setPreviewWidth(prevWidth));
          } else {
            dispatch(setPreviewWidth(30)); // Default width if previous width is too small
          }
        } else {
          // Save current width before going full width
          setPrevWidth(alertState.previewWidth);
          
          // Set to maximum width (95%) using Redux
          dispatch(setPreviewWidth(100));
        }
        setIsFullWidth(!isFullWidth);
      }
    } catch (error) {
      console.error("Error in toggleFullWidth:", error);
    }
  };



  useEffect(() => {
    console.log("AlertSummary component mounted or updated");
  },[]);


  return (
    <AlertSummaryContainer container>
      <div className="d-flex justify-content-between w-100 align-items-center mb-2">
        <div className="d-flex align-items-center">
          <strong className="text-break">{t("INCIDENTS_PAGE.INCIDENT")}: {!isLoadingGeneral && alertSummary?.id}</strong>
          {isLoadingGeneral && <Skeleton className="ms-2" style={{width: '55%', height: 36}} animation="wave" variant="text" />}
        </div>
        <div className="d-flex">
          <button
            onClick={() => toggleFullWidth()}
            style={{ color: theme.colors.textColor}}
            className={`p-2 rounded-full transition-colors`}
            title="Toggle width"
          >
            {isFullWidth ? (
              <IconMinimize size={18} />
            ) : (
              <IconMaximize size={18} />
            )}
          </button>
          <button
            onClick={() => toggleFullWidth({close: true})}
            style={{ color: theme.colors.textColor}}
            className={`p-2 rounded-full transition-colors`}
            title="Close"
          >
            <IconX size={18} />
          </button>
        </div>
      </div>

      
      { isLoadingGeneral ? <>
          <div className="d-flex flex-row my-2">
              <Skeleton  style={{width: 150, height: 52}} animation="wave" variant="text" />
              <Skeleton  className="ms-2" style={{width: 150, height: 52}} animation="wave" variant="text" />
          </div>
          </> : <>
          <div className="d-flex flex-row my-2">
            { !errorState && alertSummary ?
              <>
                <div style={{backgroundColor: statusColor ? statusColor.bk : "#7b7881"}} className="mt-2 mb-2 ps-4 pe-4 pt-2 pb-2 rounded-3 shadow">
                  <strong style={{color: "rgb(39, 55, 70)", opacity: 0.9}}>{alertSummary?.status}</strong>
                </div>
                <div style={{backgroundColor: theme.severity.range[alertSummary ? alertSummary.severity : ""]}} className="m-2 ms-2 ps-4 pe-4 pt-2 pb-2 rounded-3 shadow">
                  <strong style={{color: "rgb(39, 55, 70)", opacity: 0.9}}>{t("INCIDENTS_PAGE.SEVERITY")}: {alertSummary?.severity}</strong>
                </div>
              </> :
              <>
                <div style={{backgroundColor: "#7b7881"}} className="mt-2 mb-2 ps-4 pe-4 pt-2 pb-2 rounded-3 shadow">
                  <strong style={{color: "rgb(39, 55, 70)", opacity: 0.9}}>Status</strong>
                </div>
                <div style={{backgroundColor: "#7b7881"}} className="m-2 ms-2 ps-4 pe-4 pt-2 pb-2 rounded-3 shadow">
                  <strong style={{color: "rgb(39, 55, 70)", opacity: 0.9}}>{t("INCIDENTS_PAGE.SEVERITY")}</strong>
                </div>
              </>
            }
          </div>
        </>
      }

      {!errorState && (
        <>
          <AlertDetailsAccordion assigned={alertSummary?.assignedUsers} tags={alertSummary?.tags} org={alertSummary?.organization} lastModified={alertSummary?.lastModified || ""} createdDate={alertSummary?.createdDate || ""}/>
          
          
        </>
      )}

      
      { errorState ? 
        <div className="flex h-full w-full items-center justify-center flex-col p-6 text-center my-4">
          <div className="mb-4">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </div>
          <h3 className="text-xl font-medium mb-3">No Alert Data Found</h3>
          <p className="text-sm text-gray-500">We couldn't retrieve the data for this alert.</p>
          <ReprocessButton 
            onClick={() => RefetchAlertDetails(selectedId)}
          >
            Reprocess Alert
          </ReprocessButton>
        </div>
      : alertSummary?.description && alertSummary?.description !== "" ?
        <TextContainer container spacing={2} className="d-flex flex-column my-2">
          <strong className="mb-3" style={{fontSize: theme.font.large}}>Description:</strong>
          {/* <Markdown>{alertSummary.description}</Markdown> */}
          <CustomMarkdown content={alertSummary.description} showEditButton={false}/>
        </TextContainer> :
        isLoadingGeneral && 
          <>
            <Skeleton  style={{width: 150, height: 52}} animation="wave" variant="text" />
            <Skeleton  className="w-100" style={{height: 150}} animation="wave" variant="text" />
          </>
      } 



      {!errorState && <ActionButtonsContainer className="mb-2 pb-4">
        <strong style={{fontSize: theme.font.large}}>{t("INCIDENTS_PAGE.REMEDIATION_ACTIONS")}:</strong>
        <TextContainer container spacing={2} className="d-flex flex-wrap flex-row pt-2">
          {/* <TagsButton 
            onClick={() => console.log("")} 
            disabled={true} 
            variant="contained" 
            className="p-3 m-2" 
            style={{
              backgroundColor: '#777777',
              color: '#ffffff',
              borderRadius: '8px',
              border: 'none',
              height: '38px',
              fontSize: theme.font.medium
            }}
          >
            {t("INCIDENTS_PAGE.GENERATE_REMEDIATION")}
          </TagsButton> */}
          {/* <TagsButton 
            onClick={() => console.log("")} 
            disabled={true} 
            variant="contained" 
            className="p-3 m-2" 
            style={{
              backgroundColor: '#777777',
              color: '#ffffff',
              borderRadius: '8px',
              border: 'none',
              height: '38px',
              fontSize: theme.font.medium
            }}
          >
            {t("INCIDENTS_PAGE.DETECTION_RULE")}
          </TagsButton> */}
          {/* <TagsButton 
            onClick={() => console.log("")} 
            disabled={true} 
            variant="contained" 
            className="p-3 m-2" 
            style={{
              backgroundColor: '#777777',
              color: '#ffffff',
              borderRadius: '8px',
              border: 'none',
              height: '38px',
              fontSize: theme.font.medium
            }}
          >
            {t("INCIDENTS_PAGE.AWS_MANAGEMENT_CONSOLE")}
          </TagsButton> */}
          <TagsButton 
            onClick={() => handleTabChange(4, "actions")} 
            disabled={alertSummary?.awsCliRemediation ? false : true} 
            variant="contained" 
            className="p-3 m-2" 
            style={{
              backgroundColor: '#A100FF',
              color: '#ffffff',
              borderRadius: '8px',
              border: 'none',
              height: '38px',
              fontSize: theme.font.medium
            }}
          >
            {t("INCIDENTS_PAGE.AWS_CLI")}
          </TagsButton>
          <TagsButton 
            onClick={() => handleTabChange(5, "actions")} 
            disabled={alertSummary?.cloudformationTemplate ? false : true} 
            variant="contained" 
            className="p-3 m-2" 
            style={{
              backgroundColor: '#A100FF',
              color: '#ffffff',
              borderRadius: '8px',
              border: 'none',
              height: '38px',
              fontSize: theme.font.medium
            }}
          >
            {t("INCIDENTS_PAGE.CLOUDFORMATION_TEMPLATE")}
          </TagsButton>
          <TagsButton 
            onClick={() => handleTabChange(6, "actions")} 
            disabled={alertSummary?.terraformRemediation ? false : true} 
            variant="contained" 
            className="p-3 m-2" 
            style={{
              backgroundColor: '#A100FF',
              color: '#ffffff',
              borderRadius: '8px',
              border: 'none',
              height: '38px',
              fontSize: theme.font.medium
            }}
          >
            {t("INCIDENTS_PAGE.TERRAFORM")}
          </TagsButton>
          {/* <TagsButton 
            onClick={() => console.log("")} 
            disabled={true} 
            variant="contained" 
            className="p-3 m-2" 
            style={{
              backgroundColor: '#777777',
              color: '#ffffff',
              borderRadius: '8px',
              border: 'none',
              height: '38px',
              fontSize: theme.font.medium
            }}
          >
            {t("INCIDENTS_PAGE.GENERATE_TICKET")}
          </TagsButton> */}
        </TextContainer>

        { 
          alertSummary?.awsCliRemediation && alertSummary?.awsCliRemediation !== "" &&
          <TabPanel value={tabValue["actions"]} index={4}>
            <MarkdownContainer className="mb-4 mt-4 ps-3 pe-3">
              <AWSCLIContainer elevation={1} className="p-3">{alertSummary.awsCliRemediation}</AWSCLIContainer>
            </MarkdownContainer>
          </TabPanel>
        }
        { 
          alertSummary?.cloudformationTemplate && alertSummary?.cloudformationTemplate !== "" &&
          <TabPanel value={tabValue["actions"]} index={5} >
            <div className="mb-4 mt-4 ps-3 pe-3">
            <CustomMarkdown content={alertSummary.cloudformationTemplate} showEditButton={false}/>
            </div>
          </TabPanel>
        }
        { 
          alertSummary?.terraformRemediation && alertSummary?.terraformRemediation !== "" &&
          <TabPanel value={tabValue["actions"]} index={6} >
            <MarkdownContainer className="mb-4 mt-4 ps-3 pe-3">
              <TerraformContainer>
                <CustomMarkdown content={alertSummary.terraformRemediation} showEditButton={false}/>
              </TerraformContainer>
            </MarkdownContainer>
          </TabPanel>
        }

      </ActionButtonsContainer>}

      {!errorState && (
        <CommentsContainer className="mb-4 pt-4" style={{ display: 'block', width: '100%' }}>
          <AlertComments 
            t={t} theme={theme} 
            initialComments={alertSummary?.comments || []} 
            alertId={alertSummary?.id || ""} 
            user={user} 
          />
        </CommentsContainer>
      )}

      
      {!errorState && (
        <>
          <strong className="mt-2" style={{fontSize: theme.font.large}}>{t("INCIDENTS_PAGE.CONVERSE_WITH_AI_AC2")}:</strong>
          
          <StandaloneChatInterface 
            theme={theme}
            alertId={alertSummary?.findingid}
          />

          <TextContainer container spacing={2} className="d-flex flex-column my-2">
            <strong className="mb-3" style={{fontSize: theme.font.large}}>{t("INCIDENTS_PAGE.AI_DISCLAMER_TITLE")}:</strong>
            <p className="word-break"><strong>{t("INCIDENTS_PAGE.PLEASE_NOTE")}: </strong> 
              The information provided here has been generated by AI and, as such, may contain inaccuracies or &#39;hallucinations&#39;. We advise users to verify the accuracy of the information before relying on it for any critical decisions or actions.
            </p>
          </TextContainer>

        </>
      )}

    </AlertSummaryContainer>
  );
};

export default AlertSummary;
